import { Form, Formik, FormikProps } from 'formik';
import { useRef, useState } from 'react';
import { queryClient } from 'config/query-client';
import { message } from 'antd';

import { createTaxType, updateTaxType } from 'services/collections';
import { TaxTypes, TaxTypesCustomError } from 'types';
import { TAX_TYPES } from 'events/types';

import { Input } from './styled-components/common-components';
import useTranslation from '../../customHooks/translate';
import CustomDialog from './CustomDialog';

const defaultValues = {
  name: '',
  taxRate: 0,
};

const TaxTypesForm = ({ open, handleClose, taxNameToUpdate, onSuccess }) => {
  const [nameError, setNameError] = useState<string | null>(null);
  const [rateError, setRateError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const translation = useTranslation();
  const formRef = useRef<FormikProps<TaxTypes>>(null);

  return (
    <CustomDialog
      open={open}
      handleClose={handleClose}
      onSubmit={() => formRef.current?.handleSubmit()}
      title={translation.taxTypes.TaxType}
      buttonTitle={
        !!taxNameToUpdate
          ? `${translation.taxTypes.Update}`
          : `${translation.taxTypes.Create}`
      }
      isLoading={loading}
    >
      <Formik
        innerRef={formRef}
        initialValues={taxNameToUpdate || defaultValues}
        onSubmit={(values, actions) => {
          setLoading(true);
          const method = taxNameToUpdate ? updateTaxType : createTaxType;
          method({
            ...values,
            id: taxNameToUpdate ? taxNameToUpdate.id : undefined,
          })
            .then(() => {
              queryClient.invalidateQueries({
                queryKey: [TAX_TYPES],
              });
              handleClose();
              onSuccess(taxNameToUpdate ? 'updated' : 'added');
              setNameError(null);
              setRateError(null);
            })
            .catch((error: TaxTypesCustomError) => {
              error.response?.data?.Message &&
                message.error(error.response?.data?.Message);
              const errorMessageName = error.response?.data.errors
                ?.Name?.[0] as string | undefined;
              const errorMessageRate = error.response?.data.errors
                ?.TaxRate?.[0] as string | undefined;
              setNameError(errorMessageName || null);
              setRateError(errorMessageRate || null);
            })
            .finally(() => {
              setLoading(false);
            });
        }}
        enableReinitialize
      >
        {(props) => {
          const showEmptyTaxRate =
            !taxNameToUpdate && Number(props.values.taxRate) === 0;

          return (
            <Form>
              <form className="flex flex-col gap-[20px]">
                <div className="flex flex-col gap-[12px]">
                  <label
                    className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
                    htmlFor="name"
                  >
                    {translation.taxTypes.TaxType} *
                  </label>
                  <Input
                    className="d-block"
                    type="text"
                    name="name"
                    placeholder={translation.taxTypes.TaxType}
                    onChange={props.handleChange}
                    value={props.values.name || ''}
                    style={{
                      borderColor: nameError ? 'red' : '',
                    }}
                  />
                  {nameError && (
                    <span className="text-[12px] text-red-600">
                      {nameError}
                    </span>
                  )}
                </div>
                <div className="flex flex-col gap-[12px]">
                  <label
                    className="text-[14px] font-semibold text-[#1D1D1D] md:text-[16px]"
                    htmlFor="taxRate"
                  >
                    {translation.taxTypes.TaxRate} *
                  </label>
                  <Input
                    className="d-block"
                    type="number"
                    name="taxRate"
                    onChange={props.handleChange}
                    placeholder={translation.taxTypes.TaxRate}
                    value={showEmptyTaxRate ? '' : props.values.taxRate}
                    style={{
                      borderColor: rateError ? 'red' : '',
                    }}
                  />
                  {rateError && (
                    <span className="text-[12px] text-red-600">
                      {rateError}
                    </span>
                  )}
                </div>
              </form>
            </Form>
          );
        }}
      </Formik>
    </CustomDialog>
  );
};

export default TaxTypesForm;
